/**
 * components/hazard/ClauseRefPicker.js — ЗӨРЧСӨН ЗААЛТЫГ ЖУРМЫН ЛАВЛАХААС.
 *
 * Хоёр аргаар нэг үр дүнд хүрнэ:
 *   1) ЖУРМАА сонгоод → доторх заалтаас сонгох
 *   2) Шууд заалтын дугаар/үгээ бичих → нэр нь өөрөө гарч ирнэ
 *      (журам сонгоогүй бол БҮХ журмаас хайна)
 *
 * ⚠ ЯАГААД `xab.violation_reference` (kind="clause") ХЭРЭГЛЭЭГҮЙ ВЭ:
 *   Тэр лавлах нь ХООСОН (0 мөр) бөгөөд ХАБЭА хэлтэс гараар бөглөх ёстой
 *   байсан. Гэтэл системд ЖИНХЭНЭ эх сурвалж бэлэн байна: `xab.regulation`
 *   (1 278 журам) + `xab.regulation_clause` (77 269 заалт). Хоёр газар
 *   тусад нь заалт бүртгэвэл эшлэл салж, «Асуух» хайлт, албан шаардлагын
 *   үндэслэл хоорондоо зөрнө.
 *
 * ⚠ ТИЙМЭЭС `violation_clause_id` нь `xab.regulation_clause.id` РУУ заана
 *   (`violation_reference.id` руу БИШ). `violation_clause_text` нь
 *   «код · бичвэр» хэлбэрийн денормалчилсан хуулбар — журам хожим
 *   засагдсан ч зөрчил бүртгэгдсэн үеийн үг хэллэгээрээ үлдэнэ.
 *
 * ⚠ Заалт нь 77 мянга тул БҮГДИЙГ ТАТАХГҮЙ. Сервер тал дээр хайж,
 *   `limit`-тэй буцаана (`/xab/regulation-clauses?search=&regulation_id=`).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Select, Spin, message } from "antd";

import { xabRegulationApi, xabRegulationClauseApi } from "src/api/xabApi";

const TEXT_MAX = 600; // violation_clause_text багана

/** «2.7.5 · Өндөрт ажиллахад бүсээ …» — код нь агуулгаас чухал тул түрүүнд. */
const clauseLabel = (c) => {
  const code = (c.full_code || c.clause_no || "").trim();
  const body = (c.title || c.text || "").replace(/\s+/g, " ").trim();
  const s = code ? `${code} · ${body}` : body;
  return s.length > TEXT_MAX ? s.slice(0, TEXT_MAX - 1) + "…" : s;
};

const label = (t) => (
  <div style={{ fontSize: 12, color: "#8A94A6", marginBottom: 2 }}>{t}</div>
);

/* Журмын БҮЛГИЙН ТӨРӨЛ (regulation.category) → өнгө. Жагсаалтын мөр
   бүрийн урд өнгөт тэмдэг — «аль төрлийн журам вэ» гэдгийг нэрийг
   уншихаас өмнө хэлнэ. Өнгө ГАНЦААРАА мэдээлэл дамжуулахгүй: нэр нь
   (category_name) хажууд бичигдэнэ. */
const CATEGORY_COLORS = {
  emergency: "#DC2626",
  governance: "#7C3AED",
  health: "#0EA5E9",
  incident: "#B45309",
  inspection: "#0F6E56",
  /* ⚠ `instruction` ЭНД БАЙХГҮЙ байсан тул 668 журам (бүх журмын 34%)
     нь САРААЛ анхдагч өнгөөр гарч, хэрэглэгч «ямар нь саарал ямар нь
     нил ягаан гэх мэт өнгөөр ялгагдаж байгаа нь мэдэгдэхгүй» гэж
     мэдэгдэв (2026-11-01). ХЭМЖСЭН: 1 948 журмын — other 993 ·
     instruction 668 · governance 108 · emergency 100 … Өөрөөр хэлбэл
     85% нь саарал байсан. */
  instruction: "#0891B2",
  ppe: "#2563EB",
  risk: "#F59E0B",
  training: "#16A34A",
  work_permit: "#DB2777",
  /* ⚠ `other` нь ЗОРИУД саарал — «ангилагдаагүй» гэдгийг хэлнэ. */
  other: "#94A3B8",
};
const categoryColor = (c) => CATEGORY_COLORS[String(c || "").toLowerCase()] || "#94A3B8";

export default function ClauseRefPicker({ value = {}, onChange }) {
  // ── Журмын жагсаалт (нэр + төрөл) ────────────────────────────────
  const [regs, setRegs] = useState([]);
  const [regLoading, setRegLoading] = useState(true);
  const [regId, setRegId] = useState(null);
  const [typeFiltered, setTypeFiltered] = useState(false);
  const restored = useRef(false);
  const [extraReg, setExtraRegState] = useState(null);
  const extraRegRef = useRef(null);
  const setExtraReg = (v) => {
    extraRegRef.current = v;
    setExtraRegState(v);
  };

  /* ⚠ ЗӨРЧЛИЙН ТӨРЛӨӨР ДҮҮРГЭНЭ. Төрөл (violation_type_id) сонгогдсон
       бол тэр төрөлд ХАБЭА-ийн холбосон журмууд л ирнэ (1 278-аас
       хайхгүй). Холбоосгүй төрөл бол бүх журам, `type_filtered=false`.
       Төрөл солигдоход сонгосон журам жагсаалтад байхгүй болвол
       цэвэрлэнэ — эс бөгөөс далд шүүлттэй хуучин журмаар заалт хайна. */
  const violationTypeId =
    value.violation_type_id ||
    (Array.isArray(value.violation_type_ids) ? value.violation_type_ids[0] : null) ||
    null;

  useEffect(() => {
    let alive = true;
    setRegLoading(true);
    xabRegulationApi
      .list(violationTypeId ? { violation_type_id: violationTypeId } : {})
      .then((d) => {
        if (!alive) return;
        const items = d?.items || [];
        setRegs(items);
        setTypeFiltered(!!d?.type_filtered);
        // Сонгосон журам шүүлтэнд орохгүй бол extraReg-ээр үлдээнэ —
        // засвар нээхэд сэргээсэн журмыг далдаар цэвэрлэж болохгүй
        setRegId((cur) =>
          cur &&
          !items.some((r) => r.id === cur) &&
          !(extraRegRef.current && extraRegRef.current.id === cur)
            ? null
            : cur,
        );
      })
      .catch(() => alive && setRegs([]))
      .finally(() => alive && setRegLoading(false));
    return () => {
      alive = false;
    };
  }, [violationTypeId]);

  // ── ШИНЭ ЗААЛТ НЭМЭХ ─────────────────────────────────────────────
  // Лавлахад байхгүй заалтыг бүртгэгч маягтаасаа шууд нэмнэ
  // (source='violation_form' — ХАБЭА дараа нь засварлаж болно).
  const [adding, setAdding] = useState(false);
  const [newNo, setNewNo] = useState("");
  const [newText, setNewText] = useState("");
  const [saving, setSaving] = useState(false);

  // ── Заалтын хайлт ────────────────────────────────────────────────
  const [clauses, setClauses] = useState([]);
  const [searching, setSearching] = useState(false);
  // ⚠ Хариу нь дарааллаа сольж ирж болно. Хүсэлт бүрийг дугаарлаж,
  //   ХОЦОРЧ ирсэн хариуг хаяна — эс бөгөөс хэрэглэгч бичсээр байхад
  //   өмнөх үсгийн үр дүн дээр нь буудаг.
  const seq = useRef(0);

  const fetchClauses = useCallback((keyword, regulationId) => {
    // Журам сонгоогүй БӨГӨӨД хайлтгүй бол 77 мянган заалтыг татах
    // болно — тэр үед сонголтыг хоосон үлдээнэ.
    if (!regulationId && String(keyword || "").trim().length < 2) {
      setClauses([]);
      return;
    }
    const my = ++seq.current;
    setSearching(true);
    xabRegulationClauseApi
      .list({
        ...(regulationId ? { regulation_id: regulationId } : {}),
        ...(keyword ? { search: keyword } : {}),
        limit: 50,
      })
      .then((d) => {
        if (my !== seq.current) return;
        setClauses(d?.items || []);
      })
      .catch(() => {
        if (my === seq.current) setClauses([]);
      })
      .finally(() => {
        if (my === seq.current) setSearching(false);
      });
  }, []);

  // Журам солигдоход түүний заалтуудыг шууд харуулна
  useEffect(() => {
    fetchClauses("", regId);
  }, [regId, fetchClauses]);

  /* ── ЗАСВАР НЭЭХЭД ЖУРМАА СЭРГЭЭНЭ ────────────────────────────────
     Сонгогдсон заалт байгаа ч `regId` нь энэ компонентын дотоод төлөв
     тул засварт үргэлж хоосон эхэлдэг байв → «Дүрэм, журам» хоосон.
     Заалтуудыг ID-гаар татаж, ЭХНИЙХИЙН журмыг сонгоно; заалтуудыг
     жагсаалтад нэмснээр бүтэн текст нь ч харагдана. Нэг л удаа. */
  useEffect(() => {
    if (restored.current || regId) return;
    const ids = (() => {
      const v = value.violation_clause_ids;
      if (Array.isArray(v) && v.length) return v;
      if (typeof v === "string" && v.trim())
        return v.split(",").map((x) => Number(x)).filter(Boolean);
      return value.violation_clause_id ? [value.violation_clause_id] : [];
    })();
    if (!ids.length) return;
    restored.current = true;
    xabRegulationClauseApi
      .list({ ids: ids.join(",") })
      .then((d) => {
        const rows = d?.items || [];
        if (!rows.length) return;
        setClauses((prev) => [...rows, ...prev.filter((c) => !rows.some((r) => r.id === c.id))]);
        const first = rows.find((c) => c.id === ids[0]) || rows[0];
        if (first?.regulation_id) {
          setRegId(first.regulation_id);
          // Төрлөөр шүүгдсэн жагсаалтад байхгүй байж болно — нэрийг нь хадгална
          setExtraReg({ id: first.regulation_id, ...(first.regulation || {}) });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.violation_clause_ids, value.violation_clause_id]);

  /* ── ОЛОН ЗААЛТ (дээд тал нь 3) ─────────────────────────────────────
     Нэг зөрчил хэд хэдэн заалтыг зэрэг зөрчиж болно — өөр өөр журмынх ч
     байж болно. Тиймээс сонгосон заалтууд журмын шүүлтээс ҮЛ ХАМААРАН
     хадгалагдана: `_ids`/`_texts` нь бүгд, `_id`/`_text` нь ЭХНИЙХ
     (үндсэн — тайлан, хэвлэлт хэвээр).
     `_ids` байхгүй хуучин мөрд ганц `_id`-гаас үүсгэнэ. */
  const selIds = (() => {
    const v = value.violation_clause_ids;
    if (Array.isArray(v)) return v;
    if (typeof v === "string" && v.trim())
      return v.split(",").map((x) => Number(x)).filter(Boolean);
    return value.violation_clause_id ? [value.violation_clause_id] : [];
  })();
  const selTexts = (() => {
    const v = value.violation_clause_texts;
    if (Array.isArray(v)) return v;
    if (typeof v === "string" && v.trim()) return v.split(" | ");
    return value.violation_clause_text ? [value.violation_clause_text] : [];
  })();
  // id → хадгалсан текст (одоогийн журмын жагсаалтад байхгүй ч харуулна)
  const savedText = {};
  selIds.forEach((id, i) => {
    if (selTexts[i]) savedText[id] = selTexts[i];
  });

  const options = [
    // Сонгогдсон боловч одоогийн журмын жагсаалтад БАЙХГҮЙ заалтууд —
    // өөр журмаас сонгосон эсвэл засварлах үеийн хуучин утга
    ...selIds
      .filter((id) => !clauses.some((c) => c.id === id))
      .map((id) => ({
        value: id,
        label: savedText[id] || "(бүртгэгдсэн заалт)",
      })),
    ...clauses.map((c) => ({ value: c.id, label: clauseLabel(c) })),
  ];

  const labelOf = (id) => {
    const c = clauses.find((x) => x.id === id);
    return c ? clauseLabel(c) : savedText[id] || "";
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* ⚠ antd-гийн сонголтын мөр анхдагчаар `white-space: nowrap` тул
          урт заалт нэг мөрт шахагдана. Зөвхөн ЭНЭ цонхонд
          (`clause-tall`) мөрөөр буулгана — бусад Select хөндөгдөхгүй. */}
      <style>{`
        .clause-tall .ant-select-item-option-content {
          white-space: normal;
          word-break: break-word;
          line-height: 1.45;
          padding: 3px 0;
        }
        .clause-tall .ant-select-item {
          min-height: 34px;
          align-items: flex-start;
        }
      `}</style>
      <div>
        {label(
          <>
            Дүрэм, журам
            {typeFiltered ? (
              <span style={{ marginLeft: 6, color: "#7C3AED" }}>
                · зөрчлийн төрөлд холбогдсон {regs.length} журам
              </span>
            ) : violationTypeId ? (
              <span style={{ marginLeft: 6, color: "#94A3B8" }}>
                · энэ төрөлд журам холбоогүй — бүгд
              </span>
            ) : null}
          </>,
        )}
        <Select
          showSearch
          allowClear
          loading={regLoading}
          style={{ width: "100%" }}
          placeholder="Журмын нэр / кодоор шүүх (заавал биш)"
          value={regId ?? undefined}
          onChange={(id) => setRegId(id ?? null)}
          // Шошго нь JSX (өнгөт тэмдэгтэй) тул хайлтыг текст талбараар хийнэ
          filterOption={(input, opt) =>
            String(opt?.searchtext || "").toLowerCase().includes(input.toLowerCase())
          }
          options={[
            ...regs,
            ...(extraReg && regId === extraReg.id && !regs.some((r) => r.id === extraReg.id)
              ? [extraReg]
              : []),
          ].map((r) => {
            const text = r.code ? `${r.code} · ${r.name}` : r.name;
            const col = categoryColor(r.category);
            return {
              value: r.id,
              searchtext: `${text} ${r.category_name || ""}`,
              // Урд нь бүлгийн төрлийн ӨНГӨТ ТЭМДЭГ, дараа нь нэр; төрлийн
              // нэр бүдгээр — өнгө ганцаараа мэдээлэл дамжуулахгүй.
              label: (
                <span title={r.category_name || r.category || ""}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 9,
                      height: 9,
                      borderRadius: 5,
                      background: col,
                      marginRight: 6,
                      verticalAlign: "middle",
                    }}
                  />
                  {text}
                  {/* ⚠ БҮЛГИЙН НЭР нь ӨНГӨТЭЙГӨӨ ижил өнгөөр — өмнө нь
                      бүдэг саарал байсан тул өнгөт тэмдэг ба нэр хоёрын
                      ХОЛБОО харагддаггүй байв (хэрэглэгч 2026-11-01). */}
                  {r.category_name ? (
                    <span style={{ color: col, fontSize: 11, marginLeft: 6 }}>
                      {r.category_name}
                    </span>
                  ) : null}
                  {/* ⚠ ХҮЧИНГҮЙ ЖУРАМ — шинэчлэгдсэн (`superseded_by_id`).
                      ХЭМЖСЭН: 1 948 журмын 76 нь ийм. Хүчингүй журмын
                      заалтыг албан шаардлагад эшлэх нь ЭРХ ЗҮЙН гэм тул
                      сонголтын мөрөн дээрээ хэлнэ. */}
                  {r.superseded_by_id ? (
                    <span
                      style={{
                        color: "#B91C1C",
                        fontSize: 11,
                        marginLeft: 6,
                        fontWeight: 700,
                      }}
                    >
                      · ХҮЧИНГҮЙ
                    </span>
                  ) : null}
                </span>
              ),
            };
          })}
        />
        {/* Сонгосон журмын бүлгийн төрөл — өнгөт тэмдэгтэй */}
        {regId
          ? (() => {
              const r = regs.find((x) => x.id === regId);
              if (!r) return null;
              const col = categoryColor(r.category);
              return (
                <div style={{ marginTop: 3, fontSize: 11.5, color: "#5B6472" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 9,
                      height: 9,
                      borderRadius: 5,
                      background: col,
                      marginRight: 5,
                      verticalAlign: "middle",
                    }}
                  />
                  <span style={{ color: col }}>
                    {r.category_name || r.category || "Бүлэг тодорхойгүй"}
                  </span>
                  <span style={{ color: "#A8AFBC" }}> · журмын бүлэг</span>
                  {/* ── ХҮЧИНТЭЙ ЭСЭХ (2026-11-01, хэрэглэгч:
                      «хүчинтэй эсэх мэдэгдэхгүй байна») ─────────────
                      ⚠ ХҮЧИНГҮЙ журмын заалтыг албан шаардлагад эшлэх нь
                        эрх зүйн гэм тул ЭНД тодоор хэлнэ. Сонгохыг
                        ХОРИГЛОХГҮЙ: хуучин зөрчлийг засварлахад тэр
                        журам хэрэгтэй байж болно
                        ([[feedback_no_ai_invented_limits]]).
                      ⚠ `effective_date` нь 1 948-ийн 1 270-д бөглөгдсөн —
                        байвал л харуулна, байхгүйд «тодорхойгүй» гэж
                        хоосон мэдээлэл үүсгэхгүй. */}
                  {r.superseded_by_id ? (
                    <div style={{ color: "#B91C1C", fontWeight: 600 }}>
                      <i
                        className="fas fa-exclamation-triangle"
                        style={{ marginRight: 4 }}
                      />
                      ХҮЧИНГҮЙ — шинэчлэгдсэн журмаар солигдсон
                      {r.superseded_date
                        ? ` (${String(r.superseded_date).slice(0, 10)})`
                        : ""}
                    </div>
                  ) : (
                    <div style={{ color: "#15803D" }}>
                      <i
                        className="fas fa-check-circle"
                        style={{ marginRight: 4 }}
                      />
                      Хүчинтэй
                      {r.effective_date
                        ? ` · ${String(r.effective_date).slice(0, 10)}-ээс`
                        : ""}
                    </div>
                  )}
                </div>
              );
            })()
          : null}
        {/* ⚠ ӨНГӨНИЙ УТГЫГ ИЛТ ХЭЛНЭ: өмнө нь зөвхөн өнгөт цэг байсан
            тул «ямар нь саарал ямар нь нил ягаан» гэдэг нь ойлгогдохгүй
            байв (хэрэглэгч 2026-11-01). Өнгө нь ГАНЦААРАА мэдээлэл
            дамжуулахгүй — бүлгийн нэр нь мөрөн дээр бичигдсэн. */}
        {!regId ? (
          <div style={{ marginTop: 3, fontSize: 11.5, color: "#A8AFBC" }}>
            Өнгөт тэмдэг нь журмын БҮЛГИЙГ (хажууд нэрлэгдсэн) заана ·
            «ХҮЧИНГҮЙ» гэсэн улаан тэмдэгтэй журмыг эшлэхээс зайлсхийнэ
          </div>
        ) : null}
      </div>

      <div>
        {label("Зөрчсөн заалт")}
        <Select
          showSearch
          allowClear
          style={{ width: "100%" }}
          placeholder={
            regId
              ? "Заалт сонгох эсвэл бичиж хайх"
              : "Заалтын дугаар / үгээ бичнэ үү (ж: 2.7.5)"
          }
          // Сервер тал дээр хайдаг тул клиентийн шүүлтийг унтраана —
          // эс бөгөөс ирсэн 50 мөрөөс дахин шүүж, хоосон харагдана.
          filterOption={false}
          onSearch={(kw) => fetchClauses(kw, regId)}
          notFoundContent={
            searching ? (
              <Spin size="small" />
            ) : regId ? (
              "Энэ журамд заалт олдсонгүй"
            ) : (
              "Хоёроос дээш үсэг бичнэ үү"
            )
          }
          /* ⚠ ЗААЛТЫГ БҮТНЭЭР ХАРУУЛНА. Журмын заалт нь бусад
             сонголтоос хамаагүй урт (бүтэн өгүүлбэр) тул ердийн
             өргөнтэй, нэг мөрт цонхонд «…» болж таслагдана. Тасарсан
             эшлэлээр баталгаажуулах нь буруу заалт сонгох эрсдэлтэй.
               • `dropdownMatchSelectWidth={false}` — талбарын өргөнөөр
                 хязгаарлахаа болино
               • `listHeight` — нэг дор олон заалт харагдана
               • `clause-tall` класс нь мөрийг доош буулгана */
          dropdownMatchSelectWidth={false}
          dropdownStyle={{ maxWidth: 720, minWidth: 360 }}
          listHeight={400}
          popupClassName="clause-tall"
          mode="multiple"
          maxTagCount="responsive"
          value={selIds}
          onChange={(ids) => {
            // 3-аас илүү сонговол сүүлийнхийг хүлээж авахгүй
            const next = (ids || []).slice(0, 3);
            const texts = next.map(labelOf);
            onChange({
              violation_clause_ids: next,
              violation_clause_texts: texts,
              // Үндсэн = эхнийх. Бүгдийг цуцлахад текстийг ч цэвэрлэнэ —
              // эс бөгөөс холбоосгүй хуучин нэр үлдэж, буруу эшлэл болно.
              violation_clause_id: next[0] ?? null,
              violation_clause_text: texts[0] || null,
            });
          }}
          options={options}
        />
        {/* СОНГОСОН ЗААЛТЫН БҮТЭН БИЧВЭР.
            ⚠ antd-гийн Select нь сонгосон утгыг нэг мөрөнд шахаж
              таслана. Заалт нь ХУУЛИЙН ЭШЛЭЛ тул бүтнээр харагдах
              ёстой — бүртгэгч юуг эшилж байгаагаа баталгаажуулна. */}
        {selIds.map((id, i) => {
          const c = clauses.find((x) => x.id === id);
          const text = c ? clauseLabel(c) : savedText[id];
          const src = c?.regulation?.name;
          if (!text) return null;
          return (
            <div key={id}>
              <div
                style={{
                  marginTop: 4,
                  padding: "7px 9px",
                  background: "#F8FAFC",
                  border: "1px solid #E6E9EF",
                  // Эхнийх нь ҮНДСЭН — бусдаас ялгаж тодоор
                  borderLeft: i === 0 ? "3px solid #7C3AED" : "3px solid #C4B5FD",
                  borderRadius: 6,
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: "#334155",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflowY: "auto",
                }}
              >
                {selIds.length > 1 ? (
                  <span style={{ color: "#7C3AED", fontWeight: 700 }}>
                    {i + 1}.{" "}
                  </span>
                ) : null}
                {text}
              </div>
              {/* Аль журмынх болохыг харуулна — олон журамд ижил дугаартай
                  заалт (2.7.5) байдаг тул эх сурвалж чухал. */}
              {src ? (
                <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}>
                  Эх сурвалж: {src}
                </div>
              ) : null}
            </div>
          );
        })}
        {!selIds.length && value.violation_clause_text ? (
          <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}>
            Бүртгэгдсэн: {value.violation_clause_text}
          </div>
        ) : null}

        {/* ── ЗААЛТ НЭМЭХ ──────────────────────────────────────────
            Лавлахад байхгүй заалтыг сонгосон журамд шууд нэмнэ. Журам
            сонгоогүй бол нэмэх боломжгүй — заалт нь эх сурвалжгүй
            байж болохгүй (албан шаардлагын үндэслэл). */}
        {!adding ? (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, marginTop: 4, fontSize: 12 }}
            disabled={!regId}
            title={regId ? "" : "Эхлээд журмаа сонгоно уу"}
            onClick={() => setAdding(true)}
          >
            <i className="fas fa-plus" style={{ marginRight: 4 }} />
            Заалт нэмэх
          </Button>
        ) : (
          <div
            style={{
              marginTop: 6,
              padding: 8,
              border: "1px dashed #C4B5FD",
              borderRadius: 6,
              background: "#FAF5FF",
            }}
          >
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <Input
                size="small"
                style={{ width: 110 }}
                placeholder="Дугаар (2.7.5)"
                value={newNo}
                onChange={(e) => setNewNo(e.target.value)}
              />
              <Input.TextArea
                size="small"
                autoSize={{ minRows: 2, maxRows: 5 }}
                placeholder="Заалтын агуулга"
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <Button
                size="small"
                type="primary"
                loading={saving}
                disabled={!newText.trim()}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const res = await xabRegulationClauseApi.create({
                      regulation_id: regId,
                      clause_no: newNo.trim() || undefined,
                      text: newText.trim(),
                      source: "violation_form",
                    });
                    const c = res?.item || { id: res?.id, text: newText.trim(), clause_no: newNo.trim() };
                    // Шинэ заалтыг жагсаалтад нэмээд ШУУД сонгоно
                    setClauses((prev) => [c, ...prev.filter((x) => x.id !== c.id)]);
                    const next = [...selIds.filter((x) => x !== c.id), c.id].slice(-3);
                    const texts = next.map((id) =>
                      id === c.id ? clauseLabel(c) : labelOf(id),
                    );
                    onChange({
                      violation_clause_ids: next,
                      violation_clause_texts: texts,
                      violation_clause_id: next[0] ?? null,
                      violation_clause_text: texts[0] || null,
                    });
                    setAdding(false);
                    setNewNo("");
                    setNewText("");
                    message.success("Заалт нэмэгдэж, сонгогдлоо.");
                  } catch (e) {
                    message.error(
                      e?.response?.data?.message || "Заалт нэмж чадсангүй.",
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Нэмэх
              </Button>
              <Button size="small" onClick={() => setAdding(false)}>
                Болих
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
